class SplitHeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-split-hero-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-split-hero-dot]'));
    this.currentIndex = 0;
    this.manualPaused = false;
    this.interactionPaused = false;
    this.editorPaused = false;
    this.pointerStartX = null;
    this.controller = new AbortController();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplaySpeed = Number.parseInt(this.dataset.autoplaySpeed, 10) || 6000;

    this.classList.add('is-ready');
    this.showSlide(0, false);

    if (this.slides.length < 2) return;

    this.tabIndex = 0;
    this.bindEvents();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.controller?.abort();
  }

  get autoplayEnabled() {
    return this.dataset.autoplay === 'true' && !this.reducedMotion.matches;
  }

  bindEvents() {
    const options = { signal: this.controller.signal };

    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;

      if (button.hasAttribute('data-split-hero-next')) this.showSlide(this.currentIndex + 1);
      if (button.hasAttribute('data-split-hero-previous')) this.showSlide(this.currentIndex - 1);
      if (button.hasAttribute('data-split-hero-dot')) this.showSlide(Number(button.dataset.splitHeroDot));
      if (button.hasAttribute('data-split-hero-pause')) this.toggleAutoplay(button);
    }, options);

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.showSlide(this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
    }, options);

    this.addEventListener('pointerdown', (event) => {
      this.pointerStartX = event.clientX;
    }, options);

    this.addEventListener('pointerup', (event) => {
      if (this.pointerStartX === null) return;
      const distance = event.clientX - this.pointerStartX;
      this.pointerStartX = null;
      if (Math.abs(distance) < 40) return;
      this.showSlide(this.currentIndex + (distance < 0 ? 1 : -1));
    }, options);

    this.addEventListener('mouseenter', () => this.pauseForInteraction(), options);
    this.addEventListener('mouseleave', () => this.resumeAfterInteraction(), options);
    this.addEventListener('focusin', () => this.pauseForInteraction(), options);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.resumeAfterInteraction();
    }, options);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    }, options);

    this.reducedMotion.addEventListener('change', () => this.startAutoplay(), options);

    this.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('[data-split-hero-slide]');
      const index = this.slides.indexOf(slide);
      if (index < 0) return;
      this.editorPaused = true;
      this.stopAutoplay();
      this.showSlide(index, false);
    }, options);
  }

  showSlide(index, animate = true) {
    if (!this.slides.length) return;

    this.currentIndex = (index + this.slides.length) % this.slides.length;
    this.toggleAttribute('data-animate', animate);

    for (const [slideIndex, slide] of this.slides.entries()) {
      const active = slideIndex === this.currentIndex;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));

      for (const element of slide.querySelectorAll('a, button, input, select, textarea, [tabindex]')) {
        if (active) element.removeAttribute('tabindex');
        else element.tabIndex = -1;
      }
    }

    for (const [dotIndex, dot] of this.dots.entries()) {
      const isActive = dotIndex === this.currentIndex;
      const item = dot.closest('.dots-item');
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
        item?.classList.add('active');
      } else {
        dot.removeAttribute('aria-current');
        item?.classList.remove('active');
      }
    }
  }

  pauseForInteraction() {
    this.interactionPaused = true;
    this.stopAutoplay();
  }

  resumeAfterInteraction() {
    this.interactionPaused = false;
    this.startAutoplay();
  }

  toggleAutoplay(button) {
    this.manualPaused = !this.manualPaused;
    button.setAttribute('aria-pressed', String(this.manualPaused));

    if (this.manualPaused) this.stopAutoplay();
    else this.startAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();

    if (
      !this.autoplayEnabled ||
      this.manualPaused ||
      this.interactionPaused ||
      this.editorPaused ||
      document.hidden
    ) return;

    this.timer = window.setInterval(() => this.showSlide(this.currentIndex + 1), this.autoplaySpeed);
  }

  stopAutoplay() {
    window.clearInterval(this.timer);
    this.timer = null;
  }
}

class SplitHeroProductCard extends HTMLElement {
  connectedCallback() {
    this.variantSelect = this.querySelector('[data-split-hero-variant]');
    if (!this.variantSelect) return;

    this.controller = new AbortController();
    this.variantSelect.addEventListener('change', () => this.updateVariant(), {
      signal: this.controller.signal
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  updateVariant() {
    const option = this.variantSelect.selectedOptions[0];
    const price = this.querySelector('[data-split-hero-product-price]');
    const comparePrice = this.querySelector('[data-split-hero-product-compare]');
    const image = this.querySelector('[data-split-hero-product-image]');
    const availability = this.querySelector('[data-split-hero-availability]');
    const addButton = this.querySelector('[data-split-hero-add]');
    const viewProduct = this.querySelector('[data-split-hero-view-product]');
    if (!option || !price || !comparePrice || !availability || !addButton || !viewProduct) return;

    const available = option.dataset.available === 'true';
    const specialOrder = option.dataset.specialOrder === 'true';
    const isTradeCustomer = document.body.dataset.tradeMode === 'true';
    if (!isTradeCustomer) {
      price.textContent = option.dataset.price || '';
      comparePrice.textContent = option.dataset.comparePrice || '';
      comparePrice.hidden = !option.dataset.comparePrice;
    }
    availability.textContent = available ? availability.dataset.inStockLabel : availability.dataset.soldOutLabel;

    if (image && option.dataset.imageSrc) {
      image.src = option.dataset.imageSrc;
      image.removeAttribute('srcset');
    }

    if (option.dataset.variantUrl) {
      for (const link of this.querySelectorAll('[data-split-hero-product-link]')) {
        link.href = option.dataset.variantUrl;
      }
      viewProduct.href = option.dataset.variantUrl;
    }

    addButton.hidden = specialOrder;
    addButton.disabled = !available;
    addButton.textContent = available ? addButton.dataset.addLabel : addButton.dataset.soldOutLabel;
    viewProduct.hidden = !specialOrder;
  }
}

if (!customElements.get('split-hero-slider')) {
  customElements.define('split-hero-slider', SplitHeroSlider);
}

if (!customElements.get('split-hero-product-card')) {
  customElements.define('split-hero-product-card', SplitHeroProductCard);
}

export { SplitHeroProductCard, SplitHeroSlider };
